/* eslint-disable */
import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import api from '../utils/api';
import { peekNextNumber, advanceNumber } from '../utils/docNumbering';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import { hasPermission } from '../utils/permissions';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import SearchableSelect from '@/components/ui/SearchableSelect';
import RequestCancellationButton from '../components/RequestCancellationButton';
import DocumentViewModal from '../components/procurement/DocumentViewModal';
import DocumentPrintView from '../components/procurement/DocumentPrintView';
import { COUNTRIES, CURRENCIES, getDefaultCurrency, isLocalCountry, resolveDefaultVat } from '../utils/countryData';
import FindDuplicatesButton from '../components/FindDuplicatesButton';
import { toast } from 'sonner';
import ApprovalBar from '../components/ApprovalBar';
import DraftWatermark, { ApprovalStatusBadge } from '../components/DraftWatermark';
import { resolvePreparedBy, resolveApprovers } from '../utils/signatures';

const blankItem = () => ({ product_id: '', product_name: '', part_number: '', description: '', quantity: 1, unit_price: 0, total: 0 });
const blankPO = () => ({
  po_number: '', po_date: new Date().toISOString().split('T')[0],
  supplier_id: '', supplier_name: '', supplier_reference: '',
  country: 'Bahrain', currency: 'BHD', exchange_rate: 1.0,
  items: [blankItem()],
  subtotal: 0, vat_percentage: 10, vat_amount: 0, total_amount: 0,
  freight_charge: 0,
  notes: '', status: 'open',
});

export default function PurchaseOrders() {
  const { user } = useAuth();
  const { formatAmount, companySettings } = useCurrency();
  const { selectedFy } = useFiscalYear();
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankPO());
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSupplierId, setFilterSupplierId] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [viewing, setViewing] = useState(null);     // PO being viewed
  const [printing, setPrinting] = useState(null);   // PO being printed

  // Sync global Fiscal Year selection → list date filter.
  useEffect(() => {
    if (!selectedFy) return;
    setFromDate(selectedFy.start_date);
    setToDate(selectedFy.end_date);
  }, [selectedFy?.id]);

  const canCreate = hasPermission(user, 'purchase_orders', 'create');
  const canEdit = hasPermission(user, 'purchase_orders', 'edit');
  const canDelete = hasPermission(user, 'purchase_orders', 'delete');
  const hasActions = canEdit || canDelete;
  // "Local" supplier = supplier country matches THIS company's country
  // (multi-company white-label aware — not hardcoded to Bahrain).
  const isLocal = isLocalCountry(form.country, companySettings?.country);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [pos, sup, prods, usrs] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/suppliers'),
        api.get('/products'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setList(pos.data || []);
      setSuppliers(sup.data || []);
      setProducts(prods.data || []);
      setUsers(usrs.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  // Re-compute totals when items, country, vat %, currency, or freight changes
  useEffect(() => {
    const itemsTotal = (form.items || []).reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseFloat(it.quantity) || 0), 0);
    const vatPct = isLocal ? (parseFloat(form.vat_percentage) || 0) : 0;
    const vat = itemsTotal * vatPct / 100;
    const freight = parseFloat(form.freight_charge) || 0;
    setForm((prev) => ({
      ...prev,
      subtotal: Number(itemsTotal.toFixed(3)),
      vat_percentage: vatPct,
      vat_amount: Number(vat.toFixed(3)),
      total_amount: Number((itemsTotal + vat + freight).toFixed(3)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.items), form.country, form.vat_percentage, form.freight_charge, companySettings?.country, companySettings?.tax_percentage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) { alert('Please select a supplier'); return; }
    if (!form.po_number) { alert('PO number is required'); return; }
    if (!form.items || form.items.length === 0) { alert('Add at least one item'); return; }
    try {
      const payload = { ...form };
      if (editingId) await api.put(`/purchase-orders/${editingId}`, payload);
      else {
        await api.post('/purchase-orders', payload);
        // Bump per-company PO counter so the next "+ New PO" prefills the
        // following number.
        advanceNumber('purchase_order', payload.po_number);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(blankPO());
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save PO');
    }
  };

  const handleEdit = (po) => {
    setEditingId(po.id);
    setForm({ ...blankPO(), ...po, po_date: po.po_date?.split?.('T')[0] || po.po_date });
    setShowForm(true);
  };

  const handleDelete = async (po) => {
    if (!window.confirm(`Delete PO "${po.po_number}"?`)) return;
    try {
      await api.delete(`/purchase-orders/${po.id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const onSupplierChange = (id) => {
    const s = suppliers.find((x) => x.id === id);
    const newCountry = s?.country || form.country;
    const newCurrency = s?.currency || getDefaultCurrency(newCountry);
    setForm((prev) => ({
      ...prev,
      supplier_id: id,
      supplier_name: s?.name || '',
      country: newCountry,
      currency: newCurrency,
      // Apply the per-company VAT rule (local supplier → company's
      // tax_percentage, international → 0) so the totals row reflects the
      // correct VAT immediately after picking a supplier.
      vat_percentage: resolveDefaultVat(newCountry, companySettings),
    }));
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    items[idx].total = Number(((parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].unit_price) || 0)).toFixed(3));
    setForm({ ...form, items });
  };

  const pickProduct = (idx, pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      product_id: p.id,
      product_name: p.name,
      part_number: p.part_number || '',
      unit_price: items[idx].unit_price || p.price || 0,
    };
    items[idx].total = Number(((parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].unit_price) || 0)).toFixed(3));
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, blankItem()] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, ix) => ix !== i) });

  const filtered = list.filter((p) => {
    if (filterStatus !== 'all' && (p.status || '') !== filterStatus) return false;
    if (filterSupplierId !== 'all' && p.supplier_id !== filterSupplierId) return false;
    if (filterCurrency !== 'all' && (p.currency || '') !== filterCurrency) return false;
    if (fromDate || toDate) {
      const d = p.po_date ? new Date(p.po_date) : null;
      if (!d) return false;
      if (fromDate && d < new Date(fromDate + 'T00:00:00')) return false;
      if (toDate && d > new Date(toDate + 'T23:59:59')) return false;
    }
    if (!q) return true;
    return (p.po_number || '').toLowerCase().includes(q.toLowerCase())
        || (p.supplier_name || '').toLowerCase().includes(q.toLowerCase())
        || (p.country || '').toLowerCase().includes(q.toLowerCase());
  });
  const poCurrencies = Array.from(new Set(list.map(p => p.currency).filter(Boolean))).sort();

  return (
    <div className="p-4 space-y-4">
      <PageHeader title="Purchase Orders" subtitle="Create POs for International or Local suppliers" />

      <div className="flex flex-wrap gap-2 items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PO# / supplier / country..." className="border px-3 py-2 rounded flex-1 min-w-[240px]" data-testid="po-search" />
        {canCreate && (
          <button onClick={() => {
            setEditingId(null);
            const blank = blankPO();
            // Seed country / currency / VAT from THIS company so a fresh
            // PO defaults to a local supplier of the current tenant (not
            // hardcoded to Bahrain/BHD/10%).
            if (companySettings?.country) {
              blank.country = companySettings.country;
              blank.currency = companySettings.currency || getDefaultCurrency(companySettings.country);
              blank.vat_percentage = resolveDefaultVat(companySettings.country, companySettings);
            }
            setForm(blank);
            setShowForm(true);
            // Pre-fill PO number from the company's auto-numbering rule.
            peekNextNumber('purchase_order').then((n) => {
              if (n) setForm((prev) => prev.po_number ? prev : { ...prev, po_number: n });
            });
          }} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium" data-testid="add-po-btn">+ New PO</button>
        )}
        <FindDuplicatesButton
          module="purchase-orders"
          labelFields={['po_number', 'supplier_name', 'po_date', 'total_amount', 'currency']}
          onAfterDelete={fetchAll}
        />
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <button
            onClick={async () => {
              if (!window.confirm('Recompute total_amount on every PO using current Subtotal + VAT + Freight? This fixes legacy POs that were saved before the freight-in-total rule.')) return;
              try {
                const r = await api.post('/purchase-orders/admin/recompute-totals');
                toast.success(`Scanned ${r.data.scanned}, updated ${r.data.updated} POs`);
                fetchAll();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Recompute failed');
              }
            }}
            className="bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 px-3 py-2 rounded text-sm font-medium"
            data-testid="po-recompute-totals-btn"
            title="Re-stamp Subtotal + VAT + Freight = Total on legacy POs"
          >
            🔧 Recompute Totals
          </button>
        )}
      </div>

      {/* Advanced filters */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2" data-testid="po-advanced-filters">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="po-filter-status">
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="partial">Partial</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="po-filter-supplier">
          <option value="all">All suppliers</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filterCurrency} onChange={(e) => setFilterCurrency(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="po-filter-currency">
          <option value="all">All currencies</option>
          {poCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <DateInputDMY value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="From date" data-testid="po-filter-from-date" />
        <DateInputDMY value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="To date" data-testid="po-filter-to-date" />
        {(q || filterStatus !== 'all' || filterSupplierId !== 'all' || filterCurrency !== 'all' || fromDate || toDate) && (
          <button
            type="button"
            onClick={() => { setQ(''); setFilterStatus('all'); setFilterSupplierId('all'); setFilterCurrency('all'); setFromDate(''); setToDate(''); }}
            className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            data-testid="po-clear-filters-btn"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Filtered totals — POs have per-currency totals so we group by currency */}
      {filtered.length > 0 && (() => {
        const totalsByCur = filtered.reduce((acc, po) => {
          const cur = po.currency || 'BHD';
          acc[cur] = (acc[cur] || 0) + (po.total_amount || 0);
          return acc;
        }, {});
        return (
          <div className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200" data-testid="po-filtered-totals">
            <span className="text-xs text-gray-700">Filtered: <b>{filtered.length}</b> POs</span>
            {Object.entries(totalsByCur).map(([cur, amt]) => (
              <span key={cur} className="text-xs px-2 py-1 rounded bg-white border border-blue-200" data-testid={`po-filtered-total-${cur}`}>
                Total ({cur}): <b>{amt.toFixed(3)}</b>
              </span>
            ))}
          </div>
        );
      })()}

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left">PO #</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-left">Country</th>
              <th className="px-3 py-2 text-left">Currency</th>
              <th className="px-3 py-2 text-right">Subtotal</th>
              <th className="px-3 py-2 text-right">VAT</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Approval</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={11} className="text-center py-6 text-gray-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-6 text-gray-500">No purchase orders yet</td></tr>
            ) : filtered.map((po) => (
              <tr key={po.id} className={po.cancellation_status === 'pending' ? 'bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-200' : 'hover:bg-gray-50'}>
                <td className="px-3 py-2 font-medium text-blue-700">{po.po_number}</td>
                <td className="px-3 py-2">{formatDateDMY(po.po_date)}</td>
                <td className="px-3 py-2">{po.supplier_name}</td>
                <td className="px-3 py-2">{po.country}</td>
                <td className="px-3 py-2">{po.currency}</td>
                <td className="px-3 py-2 text-right">{(po.subtotal || 0).toFixed(3)}</td>
                <td className="px-3 py-2 text-right">{(po.vat_amount || 0).toFixed(3)}</td>
                <td className="px-3 py-2 text-right font-semibold">{(po.total_amount || 0).toFixed(3)}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    po.status === 'received' ? 'bg-green-100 text-green-700'
                    : po.status === 'partial' ? 'bg-orange-100 text-orange-700'
                    : po.status === 'cancelled' ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {po.status}
                    {po.status === 'partial' && po.balance_summary ? ` (${po.balance_summary})` : ''}
                  </span>
                  {po.cancellation_status === 'pending' && (
                    <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-semibold" title={`Cancellation pending — ${po.cancellation_reason || ''}`}>
                      🟠 Cancel pending
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <ApprovalStatusBadge status={po.approval_status || 'draft'} />
                </td>
                {canEdit && (
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => setViewing(po)} className="text-gray-700 hover:text-gray-900 text-sm mr-3" data-testid={`view-po-${po.id}`}>👁️ View</button>
                    <button onClick={() => setPrinting(po)} className="text-purple-600 hover:text-purple-800 text-sm mr-3" data-testid={`print-po-${po.id}`}>🖨 Print</button>
                    <button onClick={() => handleEdit(po)} className="text-blue-600 hover:text-blue-800 text-sm mr-3">✏️ Edit</button>
                    {canDelete && (
                      <button onClick={() => handleDelete(po)} className="text-red-600 hover:text-red-800 text-sm mr-3">🗑 Delete</button>
                    )}
                    <RequestCancellationButton docKind="purchase_order" docId={po.id} doc={po} onDone={fetchAll} />
                  </td>
                )}
                {!canEdit && (
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => setViewing(po)} className="text-gray-700 hover:text-gray-900 text-sm mr-3" data-testid={`view-po-${po.id}`}>👁️ View</button>
                    <button onClick={() => setPrinting(po)} className="text-purple-600 hover:text-purple-800 text-sm mr-3" data-testid={`print-po-${po.id}`}>🖨 Print</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="po-form-modal">
          <div className="bg-white rounded-xl w-full max-w-5xl my-8">
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
              <h2 className="font-semibold text-lg">{editingId ? 'Edit Purchase Order' : 'New Purchase Order'}</h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm mb-1">PO Number *</label>
                  <input required value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} className="w-full border rounded px-3 py-2" data-testid="po-number-input" />
                </div>
                <div>
                  <label className="block text-sm mb-1">PO Date *</label>
                  <DateInputDMY value={form.po_date} onChange={(e) => setForm({ ...form, po_date: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">Supplier *</label>
                  <SearchableSelect
                    required
                    value={form.supplier_id}
                    onChange={onSupplierChange}
                    options={suppliers.map((s) => ({ value: s.id, label: `${s.name}${s.country ? ' · ' + s.country : ''}` }))}
                    placeholder="Select supplier"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">Supplier Reference</label>
                  <input
                    value={form.supplier_reference || ''}
                    onChange={(e) => setForm({ ...form, supplier_reference: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g. Supplier quote # / their reference"
                    data-testid="po-supplier-reference-input"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Country *</label>
                  <select
                    required
                    value={form.country}
                    onChange={(e) => {
                      const newCountry = e.target.value;
                      // Auto-pair currency + VAT with the chosen country
                      // against THIS company's settings:
                      //   • local supplier (same country)  → company tax %
                      //   • international supplier         → 0 %
                      // Currency follows the country's native default
                      // (user can override afterwards).
                      setForm((prev) => ({
                        ...prev,
                        country: newCountry,
                        currency: getDefaultCurrency(newCountry),
                        vat_percentage: resolveDefaultVat(newCountry, companySettings),
                      }));
                    }}
                    className="w-full border rounded px-3 py-2 bg-white"
                    data-testid="po-country-select"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Currency *</label>
                  <select
                    required
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full border rounded px-3 py-2 bg-white"
                    data-testid="po-currency-select"
                  >
                    {/* Include current value even if it's not in our preset
                        list so we never silently lose an existing PO's currency
                        when editing. */}
                    {!CURRENCIES.includes(form.currency) && form.currency && (
                      <option value={form.currency}>{form.currency}</option>
                    )}
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">VAT %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={isLocal ? form.vat_percentage : 0}
                    disabled={!isLocal}
                    onChange={(e) => setForm({ ...form, vat_percentage: parseFloat(e.target.value) || 0 })}
                    className={`w-full border rounded px-3 py-2 ${!isLocal ? 'bg-gray-100' : ''}`}
                    data-testid="po-vat-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{isLocal
                    ? `${companySettings?.country || 'Local'} (local) — ${form.vat_percentage || 0}% VAT applies`
                    : 'International — no VAT'}</p>
                </div>
                <div>
                  <label className="block text-sm mb-1">Freight Cost ({form.currency})</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.freight_charge ?? 0}
                    onChange={(e) => setForm({ ...form, freight_charge: parseFloat(e.target.value) || 0 })}
                    className="w-full border rounded px-3 py-2"
                    data-testid="po-freight-input"
                    placeholder="0.000"
                  />
                  <p className="text-xs text-gray-500 mt-1">Captured in supplier currency. Included in the PO total and auto-fills the Purchase Entry (converted to BHD at the receipt's exchange rate).</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">Items *</h3>
                  <button type="button" onClick={addItem} className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded">+ Add Item</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={`po-item-${idx}`} className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded">
                      {/* Part # (searchable) — picking auto-fills product name */}
                      <div className="col-span-12 md:col-span-3">
                        <SearchableSelect
                          value={it.product_id}
                          onChange={(v) => pickProduct(idx, v)}
                          options={products.map((p) => ({
                            value: p.id,
                            label: p.part_number || '(no part #)',
                          }))}
                          placeholder="Part #"
                          data-testid={`po-item-part-${idx}`}
                        />
                      </div>
                      {/* Product name (searchable by name) — same pickProduct
                          handler, so picking from either column fills the
                          other. Listing every product so the user can search
                          by description without first knowing the part #. */}
                      <div className="col-span-12 md:col-span-4">
                        <SearchableSelect
                          value={it.product_id}
                          onChange={(v) => pickProduct(idx, v)}
                          options={products.map((p) => ({
                            value: p.id,
                            label: p.part_number ? `${p.name} (${p.part_number})` : p.name,
                          }))}
                          placeholder="Search product / description"
                          data-testid={`po-item-name-${idx}`}
                        />
                      </div>
                      <input
                        type="number"
                        step="0.001"
                        inputMode="decimal"
                        className="col-span-3 md:col-span-1 border rounded px-2 py-1.5 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="Qty"
                        value={it.quantity}
                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      />
                      <input
                        type="number"
                        step="0.001"
                        inputMode="decimal"
                        className="col-span-3 md:col-span-2 border rounded px-2 py-1.5 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="Unit Price"
                        value={it.unit_price}
                        onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                      />
                      <div className="col-span-5 md:col-span-1 text-right text-sm font-medium">{(it.total || 0).toFixed(3)}</div>
                      <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-red-600 hover:text-red-800">✕</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-auto max-w-md text-sm">
                <div className="flex justify-between border-b pb-1"><span>Subtotal</span><span>{form.subtotal.toFixed(3)} {form.currency}</span></div>
                <div className="flex justify-between border-b pb-1"><span>VAT ({form.vat_percentage}%)</span><span>{form.vat_amount.toFixed(3)} {form.currency}</span></div>
                {(parseFloat(form.freight_charge) || 0) > 0 && (
                  <div className="flex justify-between border-b pb-1 text-amber-700" data-testid="po-freight-totals-row"><span>Freight</span><span>{(parseFloat(form.freight_charge) || 0).toFixed(3)} {form.currency}</span></div>
                )}
                <div className="flex justify-between font-bold col-span-full text-base"><span>Total</span><span>{form.total_amount.toFixed(3)} {form.currency}</span></div>
                {form.currency !== 'BHD' && (
                  <div className="col-span-full text-xs text-gray-500 text-right italic">Exchange rate is captured at goods receipt (Purchase Entry) — not here.</div>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded" data-testid="po-save-btn">{editingId ? 'Save Changes' : 'Create PO'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View modal */}
      {viewing && (() => {
        const sup = suppliers.find((s) => s.id === viewing.supplier_id) || {};
        const supplierLines = [
          viewing.supplier_name,
          [sup.address, sup.country || viewing.country].filter(Boolean).join(', '),
          sup.phone ? `Tel: ${sup.phone}` : '',
          sup.email,
          sup.cr_number ? `CR No: ${sup.cr_number}` : '',
          sup.vat_number ? `VAT No: ${sup.vat_number}` : '',
        ];
        const bankLines = [
          sup.bank_name ? `Bank: ${sup.bank_name}` : '',
          sup.account_number ? `A/C: ${sup.account_number}` : '',
        ];
        return (
        <DocumentViewModal
          title="Purchase Order"
          docNumber={viewing.po_number}
          docDate={viewing.po_date}
          company={{
            name: companySettings?.name,
            address: companySettings?.address,
            phone: companySettings?.phone,
            email: companySettings?.email,
            tax_id: companySettings?.tax_id,
            cr_number: companySettings?.cr_number,
          }}
          parties={[
            { label: 'Supplier', lines: supplierLines },
            { label: 'Pay To (Bank)', lines: bankLines.filter(Boolean).length ? bankLines : ['Not provided'] },
          ]}
          meta={[
            { label: 'Supplier Reference', value: viewing.supplier_reference },
            { label: 'Currency', value: viewing.currency },
            { label: 'VAT %', value: `${viewing.vat_percentage || 0}%` },
            { label: 'Status', value: (viewing.status || '').toUpperCase() },
            ...(viewing.status === 'partial' && viewing.balance_summary ? [{ label: 'Balance', value: viewing.balance_summary }] : []),
          ]}
          itemColumns={[
            { key: 'part_number', label: 'Part #' },
            { key: 'product_name', label: 'Product' },
            { key: 'description', label: 'Description' },
            { key: 'quantity', label: 'Qty', align: 'right', numeric: true },
            { key: 'unit_price', label: `Unit Price (${viewing.currency})`, align: 'right', numeric: true },
            { key: 'total', label: `Total (${viewing.currency})`, align: 'right', numeric: true },
          ]}
          items={viewing.items || []}
          totals={[
            { label: 'Subtotal', value: `${(viewing.subtotal || 0).toFixed(3)} ${viewing.currency}` },
            { label: `VAT (${viewing.vat_percentage || 0}%)`, value: `${(viewing.vat_amount || 0).toFixed(3)} ${viewing.currency}` },
            ...((parseFloat(viewing.freight_charge) || 0) > 0
              ? [{ label: 'Freight', value: `${(parseFloat(viewing.freight_charge) || 0).toFixed(3)} ${viewing.currency}` }]
              : []),
            { label: 'Total', value: `${(viewing.total_amount || 0).toFixed(3)} ${viewing.currency}`, bold: true },
          ]}
          notes={viewing.notes}
          topSlot={
            <>
              <ApprovalBar
                docKind="purchase_order"
                docId={viewing.id}
                status={viewing.approval_status || 'draft'}
                lastRejectionReason={viewing.last_rejection_reason}
                onChange={async () => {
                  await fetchAll();
                  // Re-pull this PO to reflect new status in the open modal
                  try {
                    const fresh = await api.get(`/purchase-orders/${viewing.id}`);
                    setViewing(fresh.data);
                  } catch (e) { /* noop */ }
                }}
              />
              {viewing.cancellation_status === 'pending' && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid={`cancel-pending-banner-${viewing.id}`}>
                  <div className="font-semibold text-amber-900">🟠 Cancellation pending approval</div>
                  <div className="text-amber-800 mt-1"><span className="font-medium">Reason:</span> {viewing.cancellation_reason || '—'}</div>
                  {viewing.cancellation_note && (
                    <div className="text-amber-800"><span className="font-medium">Note:</span> {viewing.cancellation_note}</div>
                  )}
                  {viewing.cancellation_requested_by_name && (
                    <div className="text-xs text-amber-700 mt-1">Requested by {viewing.cancellation_requested_by_name}{viewing.cancellation_requested_at ? ` • ${formatDateDMY(viewing.cancellation_requested_at)}` : ''}</div>
                  )}
                </div>
              )}
              {viewing.cancellation_status === 'cancelled' && (
                <div className="mt-2 rounded border border-slate-300 bg-slate-100 px-3 py-2 text-sm" data-testid={`cancel-done-banner-${viewing.id}`}>
                  <div className="font-semibold text-slate-800">🚫 Purchase Order cancelled</div>
                  {viewing.cancellation_reason && (
                    <div className="text-slate-700 mt-1"><span className="font-medium">Reason:</span> {viewing.cancellation_reason}</div>
                  )}
                </div>
              )}
            </>
          }
          watermark={
            (viewing.approval_status || 'draft') !== 'approved'
              ? <DraftWatermark label={(viewing.approval_status || 'draft') === 'draft' ? 'DRAFT' : 'PENDING'} />
              : null
          }
          onClose={() => setViewing(null)}
          onPrint={() => { setPrinting(viewing); setViewing(null); }}
        />
        );
      })()}

      {/* Print preview */}
      {printing && (() => {
        const sup = suppliers.find((s) => s.id === printing.supplier_id) || {};
        const supplierLines = [
          printing.supplier_name,
          [sup.address, sup.country || printing.country].filter(Boolean).join(', '),
          sup.phone ? `Tel: ${sup.phone}` : '',
          sup.email,
          sup.cr_number ? `CR No: ${sup.cr_number}` : '',
          sup.vat_number ? `VAT No: ${sup.vat_number}` : '',
        ];
        const bankLines = [
          sup.bank_name ? `Bank: ${sup.bank_name}` : '',
          sup.account_number ? `A/C: ${sup.account_number}` : '',
        ];
        return (
        <DocumentPrintView
          title="Purchase Order"
          docNumber={printing.po_number}
          docDate={printing.po_date}
          company={companySettings}
          parties={[
            { label: 'Supplier', lines: supplierLines },
            ...(bankLines.filter(Boolean).length ? [{ label: 'Pay To (Bank)', lines: bankLines }] : []),
          ]}
          meta={[
            { label: 'Supplier Reference', value: printing.supplier_reference },
            { label: 'Currency', value: printing.currency },
            { label: 'VAT %', value: `${printing.vat_percentage || 0}%` },
            { label: 'Status', value: (printing.status || '').toUpperCase() },
          ]}
          itemColumns={[
            { key: 'part_number', label: 'Part #' },
            { key: 'product_name', label: 'Product' },
            { key: 'description', label: 'Description' },
            { key: 'quantity', label: 'Qty', align: 'right', numeric: true },
            { key: 'unit_price', label: `Unit Price`, align: 'right', numeric: true },
            { key: 'total', label: `Total`, align: 'right', numeric: true },
          ]}
          items={printing.items || []}
          totals={[
            { label: 'Subtotal', value: `${(printing.subtotal || 0).toFixed(3)} ${printing.currency}` },
            { label: `VAT (${printing.vat_percentage || 0}%)`, value: `${(printing.vat_amount || 0).toFixed(3)} ${printing.currency}` },
            ...((parseFloat(printing.freight_charge) || 0) > 0
              ? [{ label: 'Freight', value: `${(parseFloat(printing.freight_charge) || 0).toFixed(3)} ${printing.currency}` }]
              : []),
            { label: 'Total', value: `${(printing.total_amount || 0).toFixed(3)} ${printing.currency}`, bold: true },
          ]}
          notes={printing.notes}
          watermark={
            (printing.approval_status || 'draft') === 'approved'
              ? null
              : (printing.approval_status === 'draft' ? 'DRAFT' : 'PENDING')
          }
          preparedBy={resolvePreparedBy(printing, users)}
          approvers={resolveApprovers(printing, users)}
          onClose={() => setPrinting(null)}
        />
        );
      })()}
    </div>
  );
}
